import React, { useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';

const nextgenTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#6C3BFF', light: '#9B6FFF', dark: '#5020E0' },
    secondary: { main: '#8B5CF6', light: '#A78BFA', dark: '#7C3AED' },
    success: { main: '#16A34A', light: '#22C55E', dark: '#15803D' },
    warning: { main: '#D97706', light: '#F59E0B', dark: '#B45309' },
    error: { main: '#DC2626', light: '#EF4444', dark: '#B91C1C' },
    background: { default: '#F8FAFC', paper: '#FFFFFF' },
    text: {
      primary: '#111827',
      secondary: '#374151',
      disabled: '#6B7280',
    },
    divider: '#E5E7EB',
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.03em' },
    h2: { fontWeight: 800, letterSpacing: '-0.025em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, letterSpacing: '-0.015em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, letterSpacing: '-0.005em' },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.65 },
    button: { fontWeight: 700, textTransform: 'none' },
    overline: { fontWeight: 700, letterSpacing: '0.1em' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.06),0 1px 2px rgba(0,0,0,0.04)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 10,
          transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
          '&:active': { transform: 'scale(0.97)' },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 4px 16px rgba(108,59,255,0.3)', transform: 'translateY(-1px)' },
        },
        outlined: {
          '&:hover': { transform: 'translateY(-1px)' },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#FFFFFF',
            transition: 'all 0.2s ease',
            '& fieldset': { borderColor: '#E2E8F0', transition: 'border-color 0.2s ease' },
            '&:hover fieldset': { borderColor: '#CBD5E1' },
            '&.Mui-focused fieldset': { borderColor: '#6C3BFF', borderWidth: 2 },
            '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(108,59,255,0.08)' },
          },
          '& .MuiInputBase-input': { color: '#0F172A' },
          '& .MuiInputLabel-root': { color: '#94A3B8' },
          '& .MuiInputLabel-root.Mui-focused': { color: '#6C3BFF' },
        },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1E293B',
          color: '#F8FAFC',
          fontSize: '0.73rem',
          fontWeight: 500,
          borderRadius: 8,
          padding: '5px 10px',
        },
        arrow: { color: '#1E293B' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, border: '1px solid', fontWeight: 500 },
        standardError: {
          borderColor: '#FECACA',
          backgroundColor: '#FEF2F2',
          color: '#991B1B',
          '& .MuiAlert-icon': { color: '#DC2626' },
        },
        standardSuccess: {
          borderColor: '#BBF7D0',
          backgroundColor: '#F0FDF4',
          color: '#14532D',
          '& .MuiAlert-icon': { color: '#16A34A' },
        },
        standardWarning: {
          borderColor: '#FDE68A',
          backgroundColor: '#FFFBEB',
          color: '#78350F',
          '& .MuiAlert-icon': { color: '#D97706' },
        },
        standardInfo: {
          borderColor: '#BFDBFE',
          backgroundColor: '#EFF6FF',
          color: '#1E3A5F',
          '& .MuiAlert-icon': { color: '#2563EB' },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 99, overflow: 'hidden', backgroundColor: '#E2E8F0' },
        bar: { borderRadius: 99 },
      },
    },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: { backgroundColor: '#F1F5F9' },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: '#E2E8F0' } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 10, transition: 'all 0.18s ease' },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: 14,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#F8FAFC',
            color: '#64748B',
            fontWeight: 700,
            fontSize: '0.71rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            borderBottom: '1px solid #E2E8F0',
            padding: '10px 16px',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #F1F5F9',
          padding: '12px 16px',
          color: '#0F172A',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background 0.12s ease',
          '&:last-child .MuiTableCell-root': { borderBottom: 'none' },
          '&:hover': { backgroundColor: '#F8FAFC' },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { color: '#64748B', borderTop: '1px solid #E2E8F0' },
        toolbar: { minHeight: 52, paddingLeft: 16 },
        displayedRows: { color: '#64748B' },
        selectIcon: { color: '#64748B' },
        actions: { '& button': { color: '#64748B' } },
      },
    },
    MuiAvatar: {
      styleOverrides: { root: { fontWeight: 700 } },
    },
    MuiDrawer: {
      styleOverrides: { paper: { backgroundImage: 'none' } },
    },
    MuiAppBar: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiBadge: {
      styleOverrides: {
        badge: { fontWeight: 700, fontSize: '0.6rem' },
      },
    },
  },
});

const globalStyles = {
  '::-webkit-scrollbar': { width: 5, height: 5 },
  '::-webkit-scrollbar-track': { background: 'transparent' },
  '::-webkit-scrollbar-thumb': {
    background: 'rgba(108,59,255,0.2)',
    borderRadius: 3,
  },
  '::-webkit-scrollbar-thumb:hover': { background: 'rgba(108,59,255,0.35)' },
  '::selection': { backgroundColor: 'rgba(108,59,255,0.15)', color: '#0F172A' },
};

const NextGenThemeProvider = ({ children }) => {
  useEffect(() => {
    if (!document.getElementById('ngb-font')) {
      const link = document.createElement('link');
      link.id = 'ngb-font';
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap';
      document.head.insertBefore(link, document.head.firstChild);
    }
  }, []);

  return (
    <ThemeProvider theme={nextgenTheme}>
      <CssBaseline />
      <GlobalStyles styles={globalStyles} />
      {children}
    </ThemeProvider>
  );
};

export default NextGenThemeProvider;
